import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InsightsIcon from '@mui/icons-material/Insights';
import apiService from '../services/api';
import {
  UsageBreakdownItem,
  UsageMetricsResponse,
  UsageRecentSignatureRequestItem,
  UsageTrendPoint,
  UsageVersionMixItem
} from '../types';

const PRIMARY_COLOR = '#0f6dff';
const SECONDARY_COLOR = '#0f9f6e';
const ACCENT_COLOR = '#ef6c00';
const SURFACE_GRADIENT =
  'linear-gradient(135deg, rgba(15,109,255,0.08) 0%, rgba(15,159,110,0.08) 100%)';
const EMPTY_STATE_HEIGHT = 240;

interface MetricTileProps {
  label: string;
  value: string;
  helper?: string;
}

interface SummaryCardProps {
  label: string;
  period: UsageMetricsResponse['periods']['last7Days'];
  emphasisColor: string;
}

interface TimeSeriesValue {
  label: string;
  primary: number;
  secondary?: number;
}

interface TimeSeriesChartProps {
  data: TimeSeriesValue[];
  primaryLabel: string;
  primaryColor: string;
  secondaryLabel?: string;
  secondaryColor?: string;
  emptyMessage: string;
}

interface BreakdownChartProps {
  title: string;
  subtitle: string;
  items: UsageBreakdownItem[];
  emptyMessage: string;
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('en-US').format(value || 0);
}

function formatPercent(value: number) {
  return `${Number(value || 0).toFixed(1)}%`;
}

function formatDateTime(value?: string | null) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatCompactValue(value?: string | null, fallback = 'Unknown') {
  if (!value) {
    return fallback;
  }

  const trimmed = String(value).trim();
  return trimmed || fallback;
}

function formatAuthMode(value?: string | null) {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) {
    return 'Unknown';
  }

  if (normalized === 'outlook-tenant-context') {
    return 'Outlook tenant';
  }

  if (normalized === 'tenant-shared') {
    return 'Shared tenant';
  }

  if (normalized === 'user-password') {
    return 'Password login';
  }

  return normalized
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

function formatRecentRequestDetails(item: UsageRecentSignatureRequestItem) {
  if (item.status === 'success') {
    if (item.signatureLength) {
      return `${formatNumber(item.signatureLength)} signature chars returned`;
    }

    return 'Signature returned successfully';
  }

  if (item.status === 'failed') {
    return item.error || 'Signature request failed';
  }

  return 'Waiting for a completion log entry';
}

function buildRecentRequestMeta(item: UsageRecentSignatureRequestItem) {
  const parts: string[] = [];

  if (item.statusCode) {
    parts.push(`HTTP ${item.statusCode}`);
  }

  if (item.smtpRequestId) {
    parts.push(`SMTP ${item.smtpRequestId}`);
  }

  if (item.requestId) {
    parts.push(`Req ${item.requestId}`);
  }

  return parts.join(' • ');
}

function RecentSignatureRequestsTable({
  items,
  selectedScopeLabel
}: {
  items: UsageRecentSignatureRequestItem[];
  selectedScopeLabel: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mt: 3 }}>
      <Typography variant="h6">Recent signature requests</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        Latest signature request outcomes for {selectedScopeLabel.toLowerCase()}, including client type,
        manifest version, auth mode, and failure details when the request did not complete.
      </Typography>

      {items.length === 0 ? (
        <Box
          sx={{
            minHeight: EMPTY_STATE_HEIGHT,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            px: 3,
            textAlign: 'center'
          }}
        >
          No recent signature request logs were found for the current filter.
        </Box>
      ) : (
        <TableContainer
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            maxHeight: 560
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Platform</TableCell>
                <TableCell>Add-in</TableCell>
                <TableCell>Manifest</TableCell>
                <TableCell>Language</TableCell>
                <TableCell>Auth</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const resolvedAt = item.completedAt || item.lastUpdatedAt || item.requestedAt;
                const statusColor =
                  item.status === 'success'
                    ? 'success'
                    : item.status === 'failed'
                      ? 'error'
                      : 'warning';

                return (
                  <TableRow key={item.requestId} hover>
                    <TableCell sx={{ minWidth: 170, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDateTime(resolvedAt)}
                      </Typography>
                      {item.requestedAt && item.requestedAt !== resolvedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Requested {formatDateTime(item.requestedAt)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Chip
                        label={item.status}
                        size="small"
                        color={statusColor}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {item.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.requestId}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180, verticalAlign: 'top' }}>
                      {item.domain}
                    </TableCell>
                    <TableCell sx={{ minWidth: 170, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatCompactValue(item.clientApp)}
                      </Typography>
                      {item.userAgent && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          title={item.userAgent}
                          sx={{
                            display: 'block',
                            maxWidth: 220,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {item.userAgent}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, verticalAlign: 'top' }}>
                      {formatCompactValue(item.platform)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, verticalAlign: 'top' }}>
                      {formatCompactValue(item.addinVersion ? item.addinVersion.toUpperCase() : null, 'Unknown')}
                    </TableCell>
                    <TableCell sx={{ minWidth: 120, verticalAlign: 'top' }}>
                      {formatCompactValue(item.manifestVersion)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100, verticalAlign: 'top' }}>
                      {formatCompactValue(item.language ? item.language.toUpperCase() : null)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 130, verticalAlign: 'top' }}>
                      {formatAuthMode(item.authMode)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 320, verticalAlign: 'top' }}>
                      <Typography variant="body2">{formatRecentRequestDetails(item)}</Typography>
                      {buildRecentRequestMeta(item) && (
                        <Typography variant="caption" color="text.secondary">
                          {buildRecentRequestMeta(item)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function isSeriesEmpty(data: TimeSeriesValue[]) {
  return data.every((point) => point.primary === 0 && (point.secondary || 0) === 0);
}

function buildChartPath(
  values: TimeSeriesValue[],
  selector: (point: TimeSeriesValue) => number,
  maxValue: number,
  width: number,
  height: number,
  padding: { top: number; right: number; bottom: number; left: number }
) {
  if (values.length === 0) {
    return '';
  }

  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  return values
    .map((point, index) => {
      const x =
        padding.left +
        (values.length === 1 ? innerWidth / 2 : (index / (values.length - 1)) * innerWidth);
      const y =
        padding.top + innerHeight - (selector(point) / Math.max(maxValue, 1)) * innerHeight;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
}

function buildAreaPath(
  values: TimeSeriesValue[],
  maxValue: number,
  width: number,
  height: number,
  padding: { top: number; right: number; bottom: number; left: number }
) {
  if (values.length === 0) {
    return '';
  }

  const innerWidth = width - padding.left - padding.right;
  const baseline = height - padding.bottom;
  const linePath = buildChartPath(values, (point) => point.primary, maxValue, width, height, padding);
  const lastX =
    padding.left +
    (values.length === 1 ? innerWidth / 2 : innerWidth);

  return `${linePath} L ${lastX.toFixed(2)} ${baseline.toFixed(2)} L ${padding.left.toFixed(
    2
  )} ${baseline.toFixed(2)} Z`;
}

function pickAxisLabels(data: TimeSeriesValue[]) {
  if (data.length <= 5) {
    return data;
  }

  const indexes = [0, Math.floor(data.length / 4), Math.floor(data.length / 2), Math.floor((data.length * 3) / 4), data.length - 1];
  const uniqueIndexes = Array.from(new Set(indexes));
  return uniqueIndexes.map((index) => data[index]);
}

function MetricTile({ label, value, helper }: MetricTileProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        background: 'rgba(255,255,255,0.78)'
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 700 }}>
        {value}
      </Typography>
      {helper && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {helper}
        </Typography>
      )}
    </Paper>
  );
}

function SummaryCard({ label, period, emphasisColor }: SummaryCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderRadius: 2,
        height: '100%',
        borderColor: 'divider',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.96) 100%)'
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box>
          <Typography variant="overline" sx={{ color: emphasisColor, letterSpacing: '0.08em' }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {formatNumber(period.activeUsers)} active users
          </Typography>
        </Box>
        <Chip
          label={`${formatPercent(period.successRate)} success`}
          sx={{
            bgcolor: `${emphasisColor}14`,
            color: emphasisColor,
            fontWeight: 600
          }}
        />
      </Stack>

      <Box
        sx={{
          mt: 2.25,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 1.5
        }}
      >
        <MetricTile
          label="Organizations"
          value={formatNumber(period.activeOrganizations)}
          helper="Distinct active domains"
        />
        <MetricTile
          label="Signature requests"
          value={formatNumber(period.signatureRequests)}
          helper={`${formatNumber(period.successfulSignatures)} completed`}
        />
        <MetricTile
          label="Default language checks"
          value={formatNumber(period.defaultLanguageRequests)}
          helper="Auto-language lookups"
        />
        <MetricTile
          label="Requests per user"
          value={period.averageRequestsPerActiveUser.toFixed(2)}
          helper={`${formatNumber(period.loginsAccepted)} logins accepted`}
        />
      </Box>
    </Paper>
  );
}

function TimeSeriesChart({
  data,
  primaryLabel,
  primaryColor,
  secondaryLabel,
  secondaryColor,
  emptyMessage
}: TimeSeriesChartProps) {
  if (!data.length || isSeriesEmpty(data)) {
    return (
      <Box
        sx={{
          minHeight: EMPTY_STATE_HEIGHT,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          px: 3,
          textAlign: 'center'
        }}
      >
        {emptyMessage}
      </Box>
    );
  }

  const width = 720;
  const height = 240;
  const padding = { top: 16, right: 18, bottom: 26, left: 18 };
  const maxValue = Math.max(
    1,
    ...data.map((point) => Math.max(point.primary, point.secondary || 0))
  );
  const primaryPath = buildChartPath(data, (point) => point.primary, maxValue, width, height, padding);
  const secondaryPath = secondaryLabel
    ? buildChartPath(data, (point) => point.secondary || 0, maxValue, width, height, padding)
    : '';
  const areaPath = buildAreaPath(data, maxValue, width, height, padding);
  const axisLabels = pickAxisLabels(data);

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 12, height: 12, borderRadius: 999, bgcolor: primaryColor }} />
          <Typography variant="body2" color="text.secondary">
            {primaryLabel}
          </Typography>
        </Stack>
        {secondaryLabel && secondaryColor && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 12, height: 12, borderRadius: 999, bgcolor: secondaryColor }} />
            <Typography variant="body2" color="text.secondary">
              {secondaryLabel}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
        <Box
          component="svg"
          viewBox={`0 0 ${width} ${height}`}
          sx={{ width: '100%', height: 240, display: 'block' }}
        >
          {[0.25, 0.5, 0.75, 1].map((ratio) => {
            const y =
              padding.top +
              (height - padding.top - padding.bottom) -
              ratio * (height - padding.top - padding.bottom);

            return (
              <line
                key={ratio}
                x1={padding.left}
                x2={width - padding.right}
                y1={y}
                y2={y}
                stroke="#e2e8f0"
                strokeWidth="1"
              />
            );
          })}
          <path d={areaPath} fill={`${primaryColor}18`} />
          <path d={primaryPath} fill="none" stroke={primaryColor} strokeWidth="4" strokeLinejoin="round" strokeLinecap="round" />
          {secondaryPath && secondaryColor && (
            <path
              d={secondaryPath}
              fill="none"
              stroke={secondaryColor}
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
              strokeDasharray="8 8"
            />
          )}
        </Box>

        <Box
          sx={{
            mt: 0.5,
            display: 'grid',
            gridTemplateColumns: `repeat(${axisLabels.length}, minmax(0, 1fr))`,
            gap: 1
          }}
        >
          {axisLabels.map((point) => (
            <Typography key={point.label} variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
              {point.label}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function BreakdownChart({ title, subtitle, items, emptyMessage }: BreakdownChartProps) {
  const maxValue = Math.max(1, ...items.map((item) => item.signatureRequests));

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        {subtitle}
      </Typography>

      {items.length === 0 ? (
        <Box
          sx={{
            minHeight: EMPTY_STATE_HEIGHT,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            px: 3,
            textAlign: 'center'
          }}
        >
          {emptyMessage}
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {items.map((item) => {
            const width = `${(item.signatureRequests / maxValue) * 100}%`;

            return (
              <Box key={item.key}>
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatNumber(item.signatureRequests)}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    mt: 0.75,
                    height: 10,
                    borderRadius: 999,
                    bgcolor: '#e5eefc',
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width,
                      minWidth: item.signatureRequests > 0 ? 6 : 0,
                      height: '100%',
                      borderRadius: 999,
                      background: 'linear-gradient(90deg, #0f6dff 0%, #5aa0ff 100%)'
                    }}
                  />
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(item.successfulSignatures)} successful
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(item.activeUsers)} active users
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatPercent(item.successRate)} success
                  </Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

function VersionMixChart({ items }: { items: UsageVersionMixItem[] }) {
  const total = items.reduce((sum, item) => sum + item.count, 0);
  const palette = ['#0f6dff', '#0f9f6e', '#ef6c00', '#7c3aed', '#e11d48', '#475569'];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
      <Typography variant="h6">Plugin version mix</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
        Distribution of signature request traffic by add-in version over the last 30 days.
      </Typography>

      {items.length === 0 || total === 0 ? (
        <Box
          sx={{
            minHeight: EMPTY_STATE_HEIGHT,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
            px: 3,
            textAlign: 'center'
          }}
        >
          No add-in version traffic was found in the last 30 days.
        </Box>
      ) : (
        <>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              height: 22,
              borderRadius: 999,
              overflow: 'hidden',
              bgcolor: '#e2e8f0'
            }}
          >
            {items.map((item, index) => (
              <Box
                key={item.key}
                sx={{
                  width: `${(item.count / total) * 100}%`,
                  bgcolor: palette[index % palette.length],
                  minWidth: item.count > 0 ? 10 : 0
                }}
              />
            ))}
          </Box>

          <Stack spacing={1.25} sx={{ mt: 2 }}>
            {items.map((item, index) => (
              <Stack
                key={item.key}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={2}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      bgcolor: palette[index % palette.length]
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.label}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {formatNumber(item.count)} requests ({formatPercent((item.count / total) * 100)})
                </Typography>
              </Stack>
            ))}
          </Stack>
        </>
      )}
    </Paper>
  );
}

const UsageAnalyticsDashboard: React.FC = () => {
  const [metrics, setMetrics] = React.useState<UsageMetricsResponse | null>(null);
  const [selectedDomain, setSelectedDomain] = React.useState('all');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  React.useEffect(() => {
    let isActive = true;

    const loadMetrics = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await apiService.getUsageMetrics(
          selectedDomain === 'all' ? undefined : selectedDomain
        );

        if (!isActive) {
          return;
        }

        setMetrics(response);
      } catch (requestError: any) {
        if (!isActive) {
          return;
        }

        setError(
          requestError?.response?.data?.message ||
            requestError?.message ||
            'Failed to load usage analytics.'
        );
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    void loadMetrics();

    return () => {
      isActive = false;
    };
  }, [selectedDomain, refreshKey]);

  const knownDomains = metrics?.inventory.knownDomains || [];
  const last7Days = metrics?.periods.last7Days;
  const last30Days = metrics?.periods.last30Days;
  const last365Days = metrics?.periods.last365Days;
  const selectedScopeLabel =
    selectedDomain === 'all' ? 'All organizations' : selectedDomain;
  const dailyChartData: TimeSeriesValue[] = (metrics?.charts.dailyUsage30Days || []).map(
    (point: UsageTrendPoint) => ({
      label: point.label,
      primary: point.signatureRequests,
      secondary: point.successfulSignatures
    })
  );
  const monthlyChartData: TimeSeriesValue[] = (metrics?.charts.monthlyUsage12Months || []).map(
    (point: UsageTrendPoint) => ({
      label: point.label,
      primary: point.signatureRequests,
      secondary: point.activeUsers
    })
  );

  return (
    <Box>
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          background: SURFACE_GRADIENT
        }}
      >
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <InsightsIcon sx={{ color: PRIMARY_COLOR }} />
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Usage Analytics
              </Typography>
            </Stack>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 860 }}>
              Track how many people are using Admail across the last week, month, and year, how many signatures were requested and completed, and which organizations and plugin versions are driving the most traffic.
            </Typography>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', lg: 'auto' } }}>
            <FormControl sx={{ minWidth: 240 }} size="small">
              <InputLabel id="analytics-domain-label">Organization</InputLabel>
              <Select
                labelId="analytics-domain-label"
                value={selectedDomain}
                label="Organization"
                onChange={(event) => setSelectedDomain(event.target.value)}
              >
                <MenuItem value="all">All organizations</MenuItem>
                {knownDomains.map((domain) => (
                  <MenuItem key={domain} value={domain}>
                    {domain}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => setRefreshKey((current) => current + 1)}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Chip label={selectedScopeLabel} color="primary" variant="outlined" />
          {metrics && (
            <Chip
              label={`Updated ${formatDateTime(metrics.generatedAt)}`}
              variant="outlined"
            />
          )}
          {metrics?.logDirectory && (
            <Chip
              label={metrics.missing ? 'Log directory missing' : 'Server logs loaded'}
              color={metrics.missing ? 'warning' : 'success'}
              variant="outlined"
            />
          )}
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mt: 3 }}>
            {error}
          </Alert>
        )}

        {loading && !metrics ? (
          <Box
            sx={{
              minHeight: 340,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <CircularProgress />
          </Box>
        ) : metrics ? (
          <>
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  xl: 'repeat(4, minmax(0, 1fr))'
                },
                gap: 2
              }}
            >
              <MetricTile
                label="Configured organizations"
                value={formatNumber(metrics.inventory.configuredOrganizations)}
                helper="Tenant records in the admin system"
              />
              <MetricTile
                label="Admin accounts"
                value={formatNumber(metrics.inventory.adminAccounts)}
                helper="Dashboard logins configured"
              />
              <MetricTile
                label="Active users in last 30 days"
                value={formatNumber(metrics.periods.last30Days.activeUsers)}
                helper={`${formatNumber(metrics.periods.last30Days.activeOrganizations)} active organizations`}
              />
              <MetricTile
                label="Completed signatures in last 30 days"
                value={formatNumber(metrics.periods.last30Days.successfulSignatures)}
                helper={`${formatPercent(metrics.periods.last30Days.successRate)} completion rate`}
              />
            </Box>

            {metrics.missing && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                Server log directory was not found. The cards below are empty until log files become available.
              </Alert>
            )}

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' },
                gap: 2
              }}
            >
              {last7Days && (
                <SummaryCard label="Last 7 days" period={last7Days} emphasisColor={PRIMARY_COLOR} />
              )}
              {last30Days && (
                <SummaryCard label="Last 30 days" period={last30Days} emphasisColor={SECONDARY_COLOR} />
              )}
              {last365Days && (
                <SummaryCard label="Last 365 days" period={last365Days} emphasisColor={ACCENT_COLOR} />
              )}
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: '1.4fr 1fr' },
                gap: 2
              }}
            >
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6">Signature traffic, last 30 days</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
                  Daily signature requests compared with successful signature responses.
                </Typography>
                <TimeSeriesChart
                  data={dailyChartData}
                  primaryLabel="Requests"
                  primaryColor={PRIMARY_COLOR}
                  secondaryLabel="Successful signatures"
                  secondaryColor={SECONDARY_COLOR}
                  emptyMessage="No signature traffic was recorded in the last 30 days."
                />
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6">Signature volume, last 12 months</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 2 }}>
                  Monthly signature demand compared with active user count.
                </Typography>
                <TimeSeriesChart
                  data={monthlyChartData}
                  primaryLabel="Signature requests"
                  primaryColor={ACCENT_COLOR}
                  secondaryLabel="Active users"
                  secondaryColor={PRIMARY_COLOR}
                  emptyMessage="No monthly signature traffic was recorded in the last 12 months."
                />
              </Paper>
            </Box>

            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', xl: 'repeat(3, minmax(0, 1fr))' },
                gap: 2
              }}
            >
              <BreakdownChart
                title="Top organizations"
                subtitle="Domains driving the most signature activity over the last 30 days."
                items={metrics.charts.topOrganizations30Days}
                emptyMessage="No organization activity was recorded for this time range."
              />
              <BreakdownChart
                title="Top users"
                subtitle="Mailbox activity ranked by signature request volume over the last 30 days."
                items={metrics.charts.topUsers30Days}
                emptyMessage="No user activity was recorded for this time range."
              />
              <VersionMixChart items={metrics.charts.versionMix30Days} />
            </Box>

            <RecentSignatureRequestsTable
              items={metrics.recentSignatureRequests || []}
              selectedScopeLabel={selectedScopeLabel}
            />
          </>
        ) : null}
      </Paper>
    </Box>
  );
};

export default UsageAnalyticsDashboard;
